import { call, put, select, takeEvery, all, take, delay } from 'redux-saga/effects'

import { retriable } from 'utils/sagas'
import { updateColorInMeta, updateLanguageInHtml } from 'utils/dom'
import { verifyTheme } from 'utils/themes'

import * as Actions from 'actions/config'
import * as AdministrationActions from 'actions/administration'
import * as Api from 'api/bff'
import { changeDate } from 'actions/filter'
import {
  genericErrorMessage,
  genericSuccessMessage,
  setAppReady,
  setAppBusy,
  loadFromLocalStorage,
  exception,
  showAppSnackbarMessage
} from 'actions/app'
import * as Selectors from 'selectors'
import { setExportConfig } from 'actions/export'
import { setUser, reset as resetUser } from 'actions/user'
import { setSubscriptions } from 'actions/subscriptions'
import { setCustomTags } from 'actions/custom_tags'
import { setIdentitySets } from 'actions/identity_sets'
import { setCharts, setGaioCharts } from 'actions/charts'
import { setMediaReviews, setMediaReviewsTotalCount } from 'actions/media_reviews'
import { setMailingLists } from 'actions/mailing_lists'
import { setSavedSearches } from 'actions/saved_searches'
import { setDashboards } from 'actions/dashboard'
import { setPersonas } from 'actions/persona_library'
import { setAssignees, setUploadedMediaFilesTotalSize } from 'actions/content_desk'
// import { setOption } from 'actions/ga'
import { navigate } from 'actions/navigation'
import { setNotifications, setLastPage } from 'actions/notifications'
import * as CookieHelper from 'utils/cookieHelper'
import { CookieNames } from 'static/constants'

export function* fetchConfigTry() {
  yield put(setAppBusy())
  const firstConfigLoaded = yield select(Selectors.getFirstConfigLoaded)
  const startedWithDeepLink = yield select(Selectors.getStartedWithDeepLink)
  const newsradarId = yield select(Selectors.getNewsradarId)
  const accessToken = yield select(Selectors.getAccessToken)
  const timezone = yield select(Selectors.getTimezone)
  const currentPath = yield select(Selectors.getCurrentPath)
  const themes = yield select(Selectors.getThemes)

  if (accessToken && newsradarId) {
    // yield put(setOption({ key: 'dimension3', value: newsradarId }))

    const result = yield call(Api.fetchConfig, timezone)

    const {
      ccdAssignees,
      ccdMediaFilesTotalSize,
      config,
      statics,
      user,
      subscriptions,
      customTags,
      identitySets,
      charts,
      gaioCharts,
      mediaReviews,
      mailingLists,
      savedSearches,
      dashboards,
      notifications,
      personas
    } = result

    const { contentDeskConfig, ...restConfig } = config

    const selectedTheme = verifyTheme(themes, (user.darkMode ? 'dark' : 'light'), restConfig.colors)

    yield put(Actions.configRequestSuccess(result))
    yield put(setUser(user))
    yield call(updateLanguageInHtml, user.languageShort)
    yield put(Actions.setStatics(statics))
    yield put(Actions.setConfig({ ...restConfig, selectedTheme }))
    yield put(Actions.setCDeskConfig(contentDeskConfig))
    yield put(setMailingLists(mailingLists))
    yield put(setSavedSearches(savedSearches))
    yield put(setDashboards(dashboards))
    yield put(setExportConfig(restConfig))
    yield put(setSubscriptions({
      subscriptions,
      topics: statics.subscriptionsSelectableCodes
    }))
    yield put(setCustomTags(customTags))
    yield put(setIdentitySets(identitySets))
    yield put(setCharts(charts))

    if (gaioCharts) {
      yield put(setGaioCharts(gaioCharts))
    }

    const pagedMediaReviews = mediaReviews && mediaReviews.mediaReviews

    yield put(setMediaReviews(pagedMediaReviews || mediaReviews))

    if (mediaReviews && Number.isInteger(mediaReviews.totalCount)) {
      yield put(setMediaReviewsTotalCount(mediaReviews.totalCount))
    }

    yield put(setAssignees(ccdAssignees))
    yield put(setPersonas(personas))
    yield put(setNotifications(notifications.notificationsList))
    yield put(setUploadedMediaFilesTotalSize(ccdMediaFilesTotalSize))

    if (!notifications.hasMoreResults) {
      yield put(setLastPage())
    }

    if (restConfig.initialMediareviewDate) {
      const { from, to } = restConfig.initialMediareviewDate

      if (from && to) {
        yield put(changeDate({
          dateFrom: new Date(from),
          dateTo: new Date(to)
        }))
      } else {
        yield put(changeDate({
          dateFrom: null,
          dateTo: null
        }))
      }
    }

    if (restConfig.initialRoute) {
      yield call(CookieHelper.saveCookie, CookieNames.INITIAL_ROUTE, restConfig.initialRoute)

      if (!firstConfigLoaded && !startedWithDeepLink && currentPath !== restConfig.initialRoute) {
        yield put(navigate(restConfig.initialRoute))
        yield delay(50)
      }
    } else {
      yield call(CookieHelper.deleteCookie, CookieNames.INITIAL_ROUTE)
    }

    yield put(setAppReady())
    yield put(loadFromLocalStorage())
  } else {
    yield put(resetUser())
  }
}

export function* fetchConfigFail(error) {
  yield put(Actions.configRequestError(error))
  yield put(resetUser())

  if (!error.response || (error.response && error.response.statusCode !== 401)) {
    yield put(genericErrorMessage())
    yield put(exception(error))
  }
}

export const retryConfigFun = error => !(error.response && error.response.statusCode === 401)

export function* fetchConfig() {
  yield call(retriable, fetchConfigTry, fetchConfigFail, null, { retryCount: 1, retryFun: retryConfigFun })
}

export function* updateConfig() {
  try {
    const body = yield select(Selectors.getConfigUpdateBody)
    const newsradarId = yield select(Selectors.getNewsradarId)

    yield call(Api.updateConfig, body, newsradarId)
    yield call(CookieHelper.deleteCookie, CookieNames.INITIAL_ROUTE)
    yield put(Actions.updateConfigRequestSuccess())
    yield put(genericSuccessMessage())
  } catch (error) {
    yield put(Actions.updateConfigRequestError(error))
    yield put(genericErrorMessage())
    yield put(exception(error))
  }
}

export function* updateUserConfig() {
  try {
    const userConfig = yield select(Selectors.getUserConfig)

    const body = {
      id: userConfig.get('id'),
      data: {
        news_saved_search_id: userConfig.get('newsSavedSearchId'),
        profile_monitoring_saved_search_id: userConfig.get('profileMonitoringSavedSearchId'),
        news_pool_saved_search_id: userConfig.get('newsPoolSavedSearchId'),
        analysis_saved_search_id: userConfig.get('analysisSavedSearchId'),
        dashboard_id: userConfig.get('dashboardId'),
        translate_search_results: userConfig.get('translateSearchResults'),
        translate_search_results_target_lang: userConfig.get('translateSearchResultsTargetLang')
      }
    }

    const { id } = yield call(Api.updateUserConfig, body)
    yield put(Actions.updateUserConfigRequestSuccess(id))
    yield put(genericSuccessMessage())
  } catch (error) {
    yield put(Actions.updateUserConfigRequestError(error))
    yield put(genericErrorMessage())
    yield put(exception(error))
  }
}

export function* refreshContentDeskPlanUsageAndLimits() {
  const i18n = yield select(Selectors.getI18n)

  try {
    yield put(Actions.fetchCDeskConfigRequestStart())
    yield take(Actions.fetchCDeskConfigRequestSuccess)

    yield put(showAppSnackbarMessage({
      text: i18n.get('ccd_config_plan_usage_and_limits_refresh_success'),
      variant: 'success'
    }))
  } catch (error) {
    yield put(showAppSnackbarMessage({
      text: i18n.get('ccd_config_plan_usage_and_limits_refresh_error'),
      variant: 'error'
    }))
  }
}

export function* fetchContentDeskConfig() {
  try {
    const result = yield call(Api.fetchContentDeskConfig)
    yield put(Actions.setCDeskConfig(result))
    yield put(Actions.fetchCDeskConfigRequestSuccess())
  } catch (error) {
    yield put(Actions.fetchCDeskConfigRequestError())
  }
}

export function* updateContentDeskConfig() {
  try {
    const contentDeskConfig = yield select(Selectors.getCDeskConfig)

    const id = contentDeskConfig.get('id')

    const body = {
      sender_email: contentDeskConfig.get('senderEmail'),
      sender_name: contentDeskConfig.get('senderName'),
      domain: contentDeskConfig.get('domain'),
      domain_region: contentDeskConfig.get('domainRegion'),
      show_default_domains: contentDeskConfig.get('showDefaultDomains')
    }

    const result = yield call(Api.updateContentDeskConfig, id, body)

    yield put(Actions.updateCDeskConfigRequestSuccess())
    yield put(Actions.setCDeskConfig(result))
    yield put(AdministrationActions.searchAllowedDomainsStart())
    yield put(genericSuccessMessage())
  } catch (error) {
    yield put(Actions.updateCDeskConfigRequestError())
    yield put(genericErrorMessage())
    yield put(exception(error))
  }
}

export function* colorChanges() {
  const colors = yield select(Selectors.getColors)

  yield call(updateColorInMeta, colors.get('primary'))
}

export function* orderPaidVersion({ payload: id }) {
  try {
    const result = yield call(Api.createOrder, { paid_feature_id: id })

    yield put(Actions.orderPaidVersionSuccess())
    yield put(Actions.updatePaidFeatures(result))
  } catch (error) {
    yield put(Actions.orderPaidVersionError(error))
    yield put(genericErrorMessage())
    yield put(exception(error))
  }
}

export function* watchConfigStart() {
  yield takeEvery(Actions.configRequestStart, fetchConfig)
}

export function* watchConfigUpdateStart() {
  yield takeEvery(Actions.updateConfigRequestStart, updateConfig)
}

export function* watchUpdateUserConfigStart() {
  yield takeEvery(Actions.updateUserConfigRequestStart, updateUserConfig)
}

export function* watchRefreshContentDeskPlanUsageAndLimits() {
  yield takeEvery(Actions.refreshContentDeskPlanUsageAndLimits, refreshContentDeskPlanUsageAndLimits)
}

export function* watchFetchCDeskConfigRequestStart() {
  yield takeEvery(Actions.fetchCDeskConfigRequestStart, fetchContentDeskConfig)
}

export function* watchUpdateCDeskConfig() {
  yield takeEvery(Actions.updateCDeskConfigRequestStart, updateContentDeskConfig)
}

export function* watchColorChanges() {
  yield takeEvery(Actions.setConfig, colorChanges)
  yield takeEvery(Actions.updateConfig, colorChanges)
  yield takeEvery(Actions.resetColors, colorChanges)
}

export function* watchOrderPaidVersion() {
  yield takeEvery(Actions.orderPaidVersionStart, orderPaidVersion)
}

export default function* configSaga() {
  yield all([
    watchConfigStart(),
    watchConfigUpdateStart(),
    watchUpdateUserConfigStart(),
    watchColorChanges(),
    watchRefreshContentDeskPlanUsageAndLimits(),
    watchFetchCDeskConfigRequestStart(),
    watchUpdateCDeskConfig(),
    watchOrderPaidVersion()
  ])
}
